import { useCallback, useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { aceptarInvitacionRpc, leerInvitacionRpc, type InvitacionLeida } from '../lib/api-negocio';
import { LARGO_CODIGO } from '../lib/codigo';
import { useApp } from '../lib/contexto';
import { supabase } from '../lib/supabase';
import { Boton, CampoCodigo, Esqueleto, Marca, MensajeError, Tarjeta, TextoAyuda } from '../ui/base';

/**
 * Aceptación de invitación por enlace (el token viaja en la URL). Sin sesión pide el código al
 * correo invitado, creando la cuenta si no existe; con sesión de otro correo lo dice y ofrece
 * salir; con la correcta acepta sola y entra. El nombre inicial es el usuario del correo.
 */

const NOMBRE_ROL: Record<string, string> = { propietario: 'dueño', cajero: 'cajero', contador: 'contador' };

export function Invitacion() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const { sesion, recargarMembresias, elegirNegocio, salir } = useApp();
  const navegar = useNavigate();

  // undefined mientras se lee; null cuando la invitación no está vigente.
  const [invitacion, setInvitacion] = useState<InvitacionLeida | null | undefined>(undefined);
  const [codigo, setCodigo] = useState('');
  const [codigoEnviado, setCodigoEnviado] = useState(false);
  const [segundos, setSegundos] = useState(0);
  const [error, setError] = useState('');
  const [ocupado, setOcupado] = useState(false);

  const leer = useCallback(() => {
    setError('');
    setInvitacion(undefined);
    leerInvitacionRpc(token)
      .then(setInvitacion)
      .catch(() => setError('No pudimos leer la invitación. Revisa la conexión e inténtalo de nuevo.'));
  }, [token]);

  useEffect(() => {
    if (token) leer();
  }, [token, leer]);

  // Cuenta atrás entre reenvíos del código.
  useEffect(() => {
    if (segundos <= 0) return;
    const t = setTimeout(() => setSegundos(segundos - 1), 1000);
    return () => clearTimeout(t);
  }, [segundos]);

  /** El código va al correo de la invitación, nunca a uno tecleado; crea la cuenta si no existe. */
  async function enviarCodigo() {
    if (!invitacion) return;
    setOcupado(true);
    setError('');
    const { error: err } = await supabase.auth.signInWithOtp({
      email: invitacion.correo,
      options: { shouldCreateUser: true },
    });
    setOcupado(false);
    if (err) {
      setError('No pudimos enviar el código. Revisa el correo o inténtalo en un minuto.');
      return;
    }
    setSegundos(45);
    setCodigoEnviado(true);
  }

  /** Con la sesión abierta, el enrutador vuelve a traer aquí y la página pasa a pedir el nombre. */
  async function verificarCodigo(e: FormEvent) {
    e.preventDefault();
    if (!invitacion) return;
    setOcupado(true);
    setError('');
    const { error: err } = await supabase.auth.verifyOtp({
      email: invitacion.correo,
      token: codigo.trim(),
      type: 'email',
    });
    setOcupado(false);
    if (err) setError('Código incorrecto o vencido. Pide uno nuevo si pasaron más de 10 minutos.');
  }

  const aceptar = useCallback(async () => {
    setOcupado(true);
    setError('');
    try {
      const negocioId = await aceptarInvitacionRpc(token, '');
      await recargarMembresias();
      elegirNegocio(negocioId);
      navegar('/', { replace: true });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo aceptar la invitación.');
    } finally {
      setOcupado(false);
    }
  }, [token, recargarMembresias, elegirNegocio, navegar]);

  const correoSesion = sesion?.user.email?.toLowerCase() ?? null;
  const deOtroCorreo = invitacion != null && correoSesion !== null && correoSesion !== invitacion.correo;

  // Con la sesión del correo invitado la aceptación es sola: abrir el enlace y pedir el código ya
  // fueron el sí. El candado evita aceptar dos veces si el efecto vuelve a correr.
  const aceptado = useRef(false);
  useEffect(() => {
    if (!sesion || !invitacion || deOtroCorreo || aceptado.current) return;
    aceptado.current = true;
    void aceptar();
  }, [sesion, invitacion, deOtroCorreo, aceptar]);

  if (!token) {
    return (
      <p className="text-destructive p-6 font-semibold" data-e2e="invitacion-invalida">
        Este enlace de invitación no es válido.
      </p>
    );
  }

  return (
    <main className="flex min-h-full justify-center px-4 py-10">
      <div className="w-full max-w-md">
        {!sesion && (
          <div className="mb-4 text-center">
            <Marca className="text-marca-grande" variante="completo" />
            <div className="firma-degradado mx-auto mt-2 h-1 w-24 rounded-full" aria-hidden="true" />
          </div>
        )}

        <Tarjeta className="w-full space-y-3">
          {invitacion === undefined && !error && <Esqueleto lineas={4} />}

          {invitacion === undefined && error && (
            <>
              <MensajeError caja>{error}</MensajeError>
              <Boton className="w-full" onClick={leer}>
                Reintentar
              </Boton>
            </>
          )}

          {invitacion === null && (
            <p className="text-destructive font-semibold" data-e2e="invitacion-invalida">
              Esta invitación no es válida o ya venció. Pídele un enlace nuevo a quien te invitó.
            </p>
          )}

          {invitacion != null && (
            <>
              <h1 className="text-titulo font-extrabold">Te invitaron a {invitacion.negocio}</h1>
              <TextoAyuda>
                Entras como {NOMBRE_ROL[invitacion.rol] ?? invitacion.rol} con el correo{' '}
                <strong data-e2e="invitacion-correo">{invitacion.correo}</strong>.
              </TextoAyuda>

              {deOtroCorreo && (
                <>
                  <MensajeError caja e2e="invitacion-otro-correo">
                    Tu sesión es de {correoSesion} y esta invitación es para otro correo. Sal y pide el
                    código con el correo invitado.
                  </MensajeError>
                  <Boton className="w-full" onClick={() => void salir()} data-e2e="invitacion-salir">
                    Salir de esta sesión
                  </Boton>
                </>
              )}

              {!sesion && !codigoEnviado && (
                <Boton
                  grande
                  className="w-full"
                  disabled={ocupado}
                  onClick={() => void enviarCodigo()}
                  data-e2e="invitacion-enviar"
                >
                  {ocupado ? 'Enviando…' : 'Enviarme el código'}
                </Boton>
              )}

              {!sesion && codigoEnviado && (
                <form onSubmit={verificarCodigo} className="space-y-3">
                  <TextoAyuda className="bg-surface rounded-control mb-0 px-3 py-2">
                    Te enviamos un código de {LARGO_CODIGO} dígitos a <strong>{invitacion.correo}</strong>.
                    Vence en 10 minutos.
                  </TextoAyuda>
                  <CampoCodigo
                    largo={LARGO_CODIGO}
                    valor={codigo}
                    onCambio={setCodigo}
                    etiqueta="Código que te llegó"
                    e2e="invitacion-codigo"
                  />
                  <Boton type="submit" grande disabled={ocupado || codigo.length !== LARGO_CODIGO} className="w-full">
                    {ocupado ? 'Confirmando…' : 'Continuar'}
                  </Boton>
                  <button
                    type="button"
                    className="text-info w-full text-contenido underline disabled:no-underline disabled:opacity-60"
                    disabled={ocupado || segundos > 0}
                    onClick={() => void enviarCodigo()}
                    data-e2e="reenviar-codigo"
                  >
                    {segundos > 0 ? `Reenviar el código (00:${String(segundos).padStart(2, '0')})` : 'Reenviar el código'}
                  </button>
                </form>
              )}

              {sesion && !deOtroCorreo && !error && (
                <p className="text-muted font-semibold" data-e2e="invitacion-aceptando">
                  Entrando al negocio…
                </p>
              )}

              <MensajeError>{error}</MensajeError>

              {sesion && !deOtroCorreo && error && (
                <Boton className="w-full" disabled={ocupado} onClick={() => void aceptar()}>
                  Reintentar
                </Boton>
              )}
            </>
          )}
        </Tarjeta>
      </div>
    </main>
  );
}
